import { appAxios } from "@/core/utils/appAxios";
import { eff } from "@/core/utils/effect-ts-abbr";
import ColorFilter from "@/dashboard/color-filter/color-filter.vue";
import MainFilterItem from "@/dashboard/main-filter-item/main-filter-item.vue";
import { SidebarNavItem } from "@/dashboard/models/dashboard-enums";
import RangeSlider from "@/dashboard/range-slider/range-slider.vue";
import { vehiclesModel } from "@/shared/vehicles/vehicles.model";
import {
    APIResponse,
    VehicleFilterType,
    GetVehiclesBody,
    AuthenticatedVehicleFiltersDto,
    VehicleSortAttribute,
    SortDirection,
    EnumUserRole,
} from "@/shared/api-contract/api-contract";
import { appLanguageSwitcherModel } from "@/shared/components/app-language-switcher/app-language-switcher.model";
import { AppRangeSliderModel } from "@/shared/components/app-range-slider/app-range-slider-model";
import { useI18n } from "@/shared/composables/use-i18n";
import { AppLanguageCode } from "@/shared/i18n/translations";
import { pageLoaderModel } from "@/shared/models/page-loader";
import { useToast } from "primevue/usetoast";
import { debounce } from "throttle-debounce-ts";
import { shallowRef, watchEffect } from "vue";
import { Options, setup, Vue } from "vue-class-component";
import CheckBoxList from "../check-box-list/check-box-list.vue";
import DashboardSidebar from "../dashboard-sidebar/dashboard-sidebar.vue";
import { sessionModel } from "@/shared/state/session.model";
import { currentUserModel } from "@/shared/state/current-user.model";

enum MainFilter {
    mainFilter1 = "mainFilter1",
    mainFilter2 = "mainFilter2",
    mainFilter3 = "mainFilter3",
    mainFilter4 = "mainFilter4",
}

@Options({
    components: {
        "dashboard-sidebar": DashboardSidebar,
        "app-check-box-list": CheckBoxList,
        "range-slider": RangeSlider,
        "color-filter": ColorFilter,
        "main-filter-item": MainFilterItem,
    },
})
export class MySettings extends Vue {
    navItem = SidebarNavItem;
    mainFilter1: VehicleFilterType | null = null;
    mainFilter2: VehicleFilterType | null = null;
    mainFilter3: VehicleFilterType | null = null;
    mainFilter4: VehicleFilterType | null = null;

    brandModel: readonly number[] = [];
    modelModel: readonly string[] = [];
    fueltypeModel: readonly number[] = [];
    conditionModel: readonly number[] = [];
    colorModel: readonly number[] = [];
    gearboxModel: readonly number[] = [];
    firstregistrationYearModel: AppRangeSliderModel = { from: -Infinity, to: Infinity };
    damageModel: AppRangeSliderModel = { from: -Infinity, to: Infinity };
    emissionNedcModel: AppRangeSliderModel = { from: -Infinity, to: Infinity };
    emissionWltpModel: AppRangeSliderModel = { from: -Infinity, to: Infinity };
    mileageModel: AppRangeSliderModel = { from: -Infinity, to: Infinity };
    promotionModel: readonly number[] = [];
    optionsModel: readonly number[] = [];
    availabilityModel: readonly number[] = [];
    bodyworkModel: readonly number[] = [];
    bodytypeModel: readonly number[] = [];

    text =
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. " +
        "Aenean pharetra, id risus, cursus. Hac tellus etiam etiam ac.";
    i18n = setup(useI18n);
    pageLoader = pageLoaderModel;

    filtersDto: AuthenticatedVehicleFiltersDto = {
        [VehicleFilterType.brand]: [],
        [VehicleFilterType.model]: [],
        [VehicleFilterType.fueltype]: [],
        [VehicleFilterType.condition]: [],
        [VehicleFilterType.color]: [],
        [VehicleFilterType.gearbox]: [],
        [VehicleFilterType.firstregistrationYear]: [],
        [VehicleFilterType.damage]: [],
        [VehicleFilterType.emissionNedc]: [],
        [VehicleFilterType.emissionWltp]: [],
        [VehicleFilterType.mileage]: [],
        [VehicleFilterType.availability]: [],
        [VehicleFilterType.provider]: [],
        [VehicleFilterType.bodywork]: [],
        [VehicleFilterType.bodytype]: [],
        [VehicleFilterType.price]: [],
        [VehicleFilterType.advanced]: [],
    };

    activeDragFilter?: VehicleFilterType = setup(() => shallowRef());

    isFiltersBlockDropZoneActive = false;

    toast = setup(useToast);

    isLoading = false;

    async handleSave() {
        this.isLoading = true;

        vehiclesModel
            .saveDefaultFilters({
                ...vehiclesModel.defaultFilters,
                color: this.colorModel,
                emission_nedc_min: this.emissionNedcModel.from,
                emission_nedc_max: this.emissionNedcModel.to,
                emission_wltp_min: this.emissionWltpModel.from,
                emission_wltp_max: this.emissionWltpModel.to,
                mileage_min: this.mileageModel.from,
                mileage_max: this.mileageModel.to,
                gearbox: this.gearboxModel,
                damage_amount_min: this.damageModel.from,
                damage_amount_max: this.damageModel.to,
                firstregistration_year_min: this.firstregistrationYearModel.from,
                firstregistration_year_max: this.firstregistrationYearModel.to,
                availability: this.availabilityModel,
                bodywork: this.bodyworkModel,
                bodytype: this.bodytypeModel,
                /* mainFilter1: this.selectedFilters[0] || null,
                mainFilter2: this.selectedFilters[1] || null,
                mainFilter3: this.selectedFilters[2] || null,
                mainFilter4: this.selectedFilters[3] || null, */
            })
            .then(() => this.showToast("success"))
            .catch(() => this.showToast("err"))
            .finally(() => (this.isLoading = false));
    }

    get showFilter(): Readonly<Record<VehicleFilterType, boolean>> {
        return eff.fn.pipe(
            Object.values(VehicleFilterType),
            eff.array.reduce({} as Record<VehicleFilterType, boolean>, (acc, type) => {
                acc[type] = !this.isDefault(type);
                return acc;
            }),
        );
    }

    isDefault(type: VehicleFilterType): boolean {
        return (
            this.mainFilter1 === type ||
            this.mainFilter2 === type ||
            this.mainFilter3 === type ||
            this.mainFilter4 === type
        );
    }

    showToast(operation: "err" | "success") {
        switch (operation) {
            case "err": {
                this.toast.add({
                    severity: "error",
                    summary: "Error while saving filters",
                    life: 3000,
                });
                break;
            }
            case "success": {
                this.toast.add({
                    severity: "success",
                    summary: "Successfully saved filters",
                    life: 3000,
                });
                break;
            }
        }
    }

    mounted() {
        const updateCars = debounce(
            200,
            async ({
                lang,
                accessToken,
            }: {
                lang: AppLanguageCode;
                accessToken?: null | string;
            }) => {
                const response = await appAxios.value.get<APIResponse<GetVehiclesBody>>(
                    `/client/web/${accessToken ? "vehicles" : "public/vehicles-grouped"}`,
                    {
                        params: {
                            current_page: 1,
                            per_page: 0,
                            sort: SortDirection.asc,
                            sort_by: VehicleSortAttribute.price,
                            include_filters: true,
                        },
                    },
                );

                if (response.data.data.meta?.filters) {
                    this.filtersDto = response.data.data.meta.filters;
                }
            },
        );

        watchEffect(() => {
            const defaultFilters = vehiclesModel.defaultFilters;

            /* this.selectedFilters = [
            defaultFilters.mainFilter1,
                defaultFilters.mainFilter2,
                defaultFilters.mainFilter3,
                defaultFilters.mainFilter4,
            ].filter((filter): filter is VehicleFilterType => !!filter); TODO! */

            this.brandModel = defaultFilters.brand || [];
            this.modelModel = defaultFilters.model || [];
            this.fueltypeModel = defaultFilters.fueltype || [];
            this.conditionModel = defaultFilters.condition || [];
            this.colorModel = defaultFilters.color || [];
            this.gearboxModel = defaultFilters.gearbox || [];
            this.firstregistrationYearModel = {
                from: defaultFilters.firstregistration_year_min || -Infinity,
                to: defaultFilters.firstregistration_year_max || Infinity,
            };
            this.damageModel = {
                from: defaultFilters.damage_amount_min || -Infinity,
                to: defaultFilters.damage_amount_max || Infinity,
            };
            this.emissionNedcModel = {
                from: defaultFilters.emission_nedc_min || -Infinity,
                to: defaultFilters.emission_nedc_max || Infinity,
            };
            this.emissionWltpModel = {
                from: defaultFilters.emission_wltp_min || -Infinity,
                to: defaultFilters.emission_wltp_max || Infinity,
            };
            this.mileageModel = {
                from: defaultFilters.mileage_min || -Infinity,
                to: defaultFilters.mileage_max || Infinity,
            };
            this.availabilityModel = defaultFilters.availability || [];
            this.bodyworkModel = defaultFilters.bodywork || [];
            this.bodytypeModel = defaultFilters.bodytype || [];
        });

        watchEffect(async () => {
            updateCars({
                lang: appLanguageSwitcherModel.lang.value,
                accessToken:
                    currentUserModel.userDto.value &&
                    currentUserModel.userDto.value?.role.id >= EnumUserRole.VIEWER
                        ? sessionModel.sessionDto.value?.token
                        : undefined,
            });
        });

        this.pageLoader.toggle(false);
    }

    beforeMount() {
        this.pageLoader.toggle(true);
    }

    get mainFilter1Model() {
        return this.mainFilter1 ? this[`${this.mainFilter1}Model` as const] : null;
    }

    get mainFilter2Model() {
        return this.mainFilter2 ? this[`${this.mainFilter2}Model` as const] : null;
    }

    get mainFilter3Model() {
        return this.mainFilter3 ? this[`${this.mainFilter3}Model` as const] : null;
    }

    get mainFilter4Model() {
        return this.mainFilter4 ? this[`${this.mainFilter4}Model` as const] : null;
    }

    get mainFilter1Options() {
        return this.mainFilter1 ? this.filtersDto[this.mainFilter1] : null;
    }

    get mainFilter2Options() {
        return this.mainFilter2 ? this.filtersDto[this.mainFilter2] : null;
    }

    get mainFilter3Options() {
        return this.mainFilter3 ? this.filtersDto[this.mainFilter3] : null;
    }

    get mainFilter4Options() {
        return this.mainFilter4 ? this.filtersDto[this.mainFilter4] : null;
    }

    updateMainFilter1(value: unknown) {
        /** if (this.mainFilter1) {
            this[`${this.mainFilter1}Model`] = value as readonly number[] &
                AppRangeSliderModel &
                readonly VehicleCondition[];
        } */
    }

    updateMainFilter2(value: unknown) {
        /* if (this.mainFilter2) {
            this[`${this.mainFilter2}Model`] = value as readonly number[] &
                AppRangeSliderModel &
                readonly VehicleCondition[];
        } */
    }

    updateMainFilter3(value: unknown) {
        /* if (this.mainFilter3) {
            this[`${this.mainFilter3}Model`] = value as readonly number[] &
                AppRangeSliderModel &
                readonly VehicleCondition[];
        } */
    }

    updateMainFilter4(value: unknown) {
        /* if (this.mainFilter4) {
            this[`${this.mainFilter4}Model`] = value as readonly number[] &
                AppRangeSliderModel &
                readonly VehicleCondition[];
        } */
    }

    onFilterDrag(filter: VehicleFilterType, event: DragEvent) {
        this.activeDragFilter = filter;
    }

    onFilterDragEnd(filter: VehicleFilterType, event: DragEvent) {
        this.activeDragFilter = undefined;
        this.isFiltersBlockDropZoneActive = false;
    }

    onMainFilterDrop(filter: MainFilter, event: DragEvent) {
        if (!this.activeDragFilter) {
            return;
        }

        if (!this.showFilter[filter]) {
            const fromMainFilter = {
                [this.mainFilter1 || ""]: MainFilter.mainFilter1,
                [this.mainFilter2 || ""]: MainFilter.mainFilter2,
                [this.mainFilter3 || ""]: MainFilter.mainFilter3,
                [this.mainFilter4 || ""]: MainFilter.mainFilter4,
            }[this.activeDragFilter];

            this[fromMainFilter] = this[filter];
        }

        this[filter] = this.activeDragFilter;
    }

    onFiltersBlockDrop(event: DragEvent) {
        if (!this.activeDragFilter || this.showFilter[this.activeDragFilter]) {
            return;
        }

        const fromMainFilter = {
            [this.mainFilter1 || ""]: MainFilter.mainFilter1,
            [this.mainFilter2 || ""]: MainFilter.mainFilter2,
            [this.mainFilter3 || ""]: MainFilter.mainFilter3,
            [this.mainFilter4 || ""]: MainFilter.mainFilter4,
        }[this.activeDragFilter];

        this[fromMainFilter] = null;
    }

    onFiltersBlockDragOver(event: DragEvent) {
        if (!this.activeDragFilter || this.showFilter[this.activeDragFilter]) {
            return;
        }

        event.preventDefault();
    }
}
